import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { forkJoin, Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import BudgetType from '../../../../../enums/budget-type';
import ClassifierGroup from '../../../../../enums/classifier-group';
import Classifier from '../../../../../enums/classifier';
import ClassifierPropertyKey from '../../../../../enums/classifier-property-key';
import { FarmUser } from '../../../../../interfaces/farm-user';
import { useService } from '../../../../../services/services';
import { can } from '../../../../../services/utils';
import DashboardLoading from '../../../../dashboard/components/dashboard-loading';
import { ButtonComponent } from '../../../../../components/form/elements/button';
import { Option } from '../../../../../components/form/elements/select';
import { budgetBlueprint, IBudgetItem } from '../../blueprint';
import TableHeadings from './table-headings';
import AddButton, { ICategories } from './add-button';
import CostsTable from './costs-table';
import CategorizedCostsTable from './categorized-costs-table';
import RevenuesTable from './revenues-table';
import css from '../../index.module.css';

interface Props {
  revenuesName: string;
  costsName: string;
  categorizedCostsName: string;
}

export default function SubBudget({
  revenuesName,
  costsName,
  categorizedCostsName,
}: Props) {
  const { t } = useTranslation();
  const { budgetService, classifierService } = useService();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const isMounted = useRef(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingData, setIsSavingData] = useState(false);
  const [error, setError] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [revenues, setRevenues] = useState(null);
  const [costs, setCosts] = useState(null);
  const [categorizedCosts, setCategorizedCosts] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const initialCategories: ICategories = {};
  const [categories, setCategories] = useState(initialCategories);
  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const addCategory = useCallback(
    (val: Option) => {
      setCategories({ ...categories, [val.value]: val.label });
      setAvailableCategories((state: Array<Option>) => {
        return state.filter((option) => option.value !== val.value);
      });
    },
    [categories]
  );

  const removeCategory = useCallback(
    (item: IBudgetItem) => {
      const categoryId = item.name.split('_').pop();
      const updatedCategories = { ...categories };
      delete updatedCategories[categoryId];
      setCategories(updatedCategories);
      setAvailableCategories((state: Array<Option>) => {
        return state.concat({
          value: categoryId,
          label: categories[categoryId],
        });
      });
    },
    [categories]
  );

  useEffect(() => {
    setIsLoading(true);

    const requests: Observable<any>[] = [budgetService.index(farmId, season)];

    if (revenuesName === BudgetType.REVENUES_CROP) {
      requests.push(
        classifierService.values(
          ClassifierGroup.WAREHOUSE,
          Classifier.PRODUCT_CATEGORIES_AND_TYPES,
          { without_not_applicable: true }
        )
      );
    } else {
      requests.push(
        classifierService.values(ClassifierGroup.LIVESTOCK, Classifier.ANIMALS)
      );
    }

    const $subscription = forkJoin(requests).subscribe(
      ([budget, categoriesData]) => {
        let categories: any = [];
        if (revenuesName === BudgetType.REVENUES_CROP) {
          categories = categoriesData.map((obj: any) => ({
            value: obj.id,
            label: obj.value,
          }));
        } else {
          categories = categoriesData.map((obj: any) => {
            let animal_type: string = null;
            Object.keys(obj.properties).forEach((key) => {
              if (
                obj.properties[key]?.property?.key ===
                ClassifierPropertyKey.ANIMAL_TYPE
              ) {
                animal_type = t(
                  'pages.finance.budget.animal_type.' +
                    obj.properties[key].value
                );
              }
            });
            return {
              value: obj.id,
              label: animal_type
                ? obj.value + ' (' + animal_type + ')'
                : obj.value,
            };
          });
        }

        setAllCategories(categories);
        if (Array.isArray(budget) && budget.length) {
          setInitialData(budget);
        } else {
          setInitialData(null);
        }

        setAvailableCategories(categories);
        setIsLoading(false);
      }
    );

    return () => {
      $subscription.unsubscribe();
      isMounted.current = false;
    };
  }, [
    farmId,
    season,
    budgetService,
    revenuesName,
    costsName,
    classifierService,
    t,
  ]);

  useEffect(() => {
    if (!initialData || !initialData.length) {
      setCategories({});
      return;
    }

    const categorizedRevenues = initialData.filter((item: any) =>
      item.name.includes(revenuesName + '_')
    );
    const _categories: any = {};
    categorizedRevenues.forEach((item: any) => {
      _categories[item.category_id] = item.categoryValue;
    });
    const _availableCategories = allCategories.filter(
      (category) => !_categories.hasOwnProperty(category.value)
    );

    setCategories(_categories);
    setAvailableCategories(_availableCategories);
  }, [initialData, allCategories, revenuesName]);

  useEffect(() => {
    const revenuesBlueprint = JSON.parse(
      JSON.stringify(budgetBlueprint[revenuesName])
    );
    const costsBlueprint = JSON.parse(
      JSON.stringify(budgetBlueprint[costsName])
    );
    const categorizedCostsBlueprint = JSON.parse(
      JSON.stringify(budgetBlueprint[costsName])
    );

    revenuesBlueprint.level = 1;
    revenuesBlueprint.children = [];
    revenuesBlueprint.calculable = [];
    costsBlueprint.level = 1;
    Object.keys(costsBlueprint.children).forEach((cost: string) => {
      costsBlueprint.children[cost].level = 2;
      costsBlueprint.children[cost].children = [];
      costsBlueprint.children[cost].calculable = [];
      costsBlueprint.children[cost].value = 0;
    });
    categorizedCostsBlueprint.level = 1;
    categorizedCostsBlueprint.name = categorizedCostsName;
    categorizedCostsBlueprint.value = 0;
    categorizedCostsBlueprint.children = {};
    categorizedCostsBlueprint.calculable = [];

    if (Object.keys(categories) && Object.keys(categories).length) {
      Object.keys(categories).forEach((category: string) => {
        let name = revenuesName + '_' + category;
        revenuesBlueprint.children.push({
          name,
          title: categories[category],
          value: 0,
          level: 2,
        });
        revenuesBlueprint.calculable.push(name);

        Object.keys(costsBlueprint.children).forEach((cost: string) => {
          let name = cost + '_' + category;
          costsBlueprint.children[cost].children.push({
            name,
            title: categories[category],
            value: 0,
            level: 3,
          });
          costsBlueprint.children[cost].calculable.push(name);
        });

        let _categorizedCostsName = categorizedCostsName + '_' + category;
        categorizedCostsBlueprint.children[_categorizedCostsName] = {
          name: _categorizedCostsName,
          title: categories[category],
          value: 0,
          level: 2,
          parent: categorizedCostsName,
          calculable: [],
        };
        Object.keys(costsBlueprint.children).forEach((cost: string) => {
          let name = cost + '_' + category;
          categorizedCostsBlueprint.children[
            _categorizedCostsName
          ].calculable.push(name);
          categorizedCostsBlueprint.calculable.push(name);
        });
      });
    }

    if (initialData && initialData.length) {
      const dataMap: any = {};
      initialData.forEach((item: any) => {
        dataMap[item.name] = item;
      });

      if (dataMap[revenuesName]) {
        revenuesBlueprint.value = dataMap[revenuesName].value;
        revenuesBlueprint.id = dataMap[revenuesName].id;
        revenuesBlueprint.children.forEach((child: any) => {
          if (dataMap[child.name]) {
            child.value = dataMap[child.name].value;
            child.id = dataMap[child.name].id;
          }
        });
      }

      if (dataMap[costsName]) {
        costsBlueprint.id = dataMap[costsName].id;
        Object.keys(costsBlueprint.children).forEach((childName: any) => {
          if (dataMap[childName]) {
            const child = costsBlueprint.children[childName];
            child.id = dataMap[childName].id;

            child.children.forEach((subChild: any) => {
              if (dataMap[subChild.name]) {
                subChild.value = parseFloat(dataMap[subChild.name].value);
                subChild.id = dataMap[subChild.name].id;
              }
            });
          }
        });
      }

      if (dataMap[categorizedCostsBlueprint.name]) {
        categorizedCostsBlueprint.id =
          dataMap[categorizedCostsBlueprint.name].id;
        Object.keys(categorizedCostsBlueprint.children).forEach(
          (childName: any) => {
            if (dataMap[childName]) {
              const child = categorizedCostsBlueprint.children[childName];
              child.id = dataMap[childName].id;
            }
          }
        );
      }
    }

    setRevenues(revenuesBlueprint);
    setCosts(costsBlueprint);
    setCategorizedCosts(categorizedCostsBlueprint);
  }, [categories, initialData, costsName, revenuesName, categorizedCostsName]);

  const formMethods = useForm({
    resolver: null,
    mode: 'onChange',
    shouldUnregister: true,
    reValidateMode: 'onChange',
    defaultValues: null,
  });
  const { handleSubmit } = formMethods;

  const onSubmit = useCallback(
    (formData: any) => {
      function addChildBudgetData(childBudget: any, submitData: any) {
        submitData[childBudget.name] = JSON.parse(JSON.stringify(childBudget));
        submitData[childBudget.name].value =
          parseFloat(formData[childBudget.name]) || 0;
        submitData[childBudget.name].value_ha = 0;
        if (!childBudget.children) {
          let parts = childBudget.name.split('_');
          submitData[childBudget.name].category_id = parts.pop();
          submitData[childBudget.name].categoryValue =
            submitData[childBudget.name].title;
          submitData[childBudget.name].parent = parts.join('_');
        }
      }

      function addSubmitData(_budgets: any, submitData: any) {
        if (_budgets.children) {
          if (Array.isArray(_budgets.children)) {
            _budgets.children.forEach((childBudget: any) =>
              addChildBudgetData(childBudget, submitData)
            );
          } else {
            Object.keys(_budgets.children).forEach((childKey: string) => {
              const childBudget = _budgets.children[childKey];
              addChildBudgetData(childBudget, submitData);

              if (childBudget.children) {
                Object.keys(childBudget.children).forEach(
                  (subChildKey: string) => {
                    const subChildBudget = childBudget.children[subChildKey];
                    addChildBudgetData(subChildBudget, submitData);
                  }
                );
              }
            });
          }
        }
        submitData[_budgets.name] = JSON.parse(JSON.stringify(_budgets));
        submitData[_budgets.name].value =
          parseFloat(formData[_budgets.name]) || 0;
        submitData[_budgets.name].value_ha = 0;
        delete submitData[_budgets.name].level;
      }

      if (allowedToStore(farmUser)) {
        setIsSavingData(true);
        setError(null);

        const submitData: any = {};
        addSubmitData(revenues, submitData);
        addSubmitData(costs, submitData);
        addSubmitData(categorizedCosts, submitData);

        const removeData: any = { budgets: [] };
        initialData &&
          initialData.length &&
          initialData.forEach((item: any) => {
            if (!submitData[item.name]) {
              const parentName = getBudgetParentName(item.name);
              if (
                parentName &&
                (parentName.includes(categorizedCostsName) ||
                  parentName.includes(costsName) ||
                  parentName.includes(revenuesName))
              ) {
                removeData.budgets.push(item);
              }
            }
          });

        const submitDataArray: any = { budgets: [] };
        Object.keys(submitData).forEach((key: string) => {
          submitDataArray.budgets.push(submitData[key]);
        });

        const $subscription = budgetService
          .destroy(farmId, season, removeData)
          .pipe(
            concatMap(() =>
              budgetService.store(farmId, season, submitDataArray)
            )
          )
          .subscribe(
            (response) => {
              if (isMounted.current) {
                setInitialData(response);
                setIsSavingData(false);
              }
            },
            () => {
              if (isMounted.current) {
                setIsSavingData(false);
                setError(t('pages.finance.budget.error'));
              }
            }
          );
        return () => $subscription.unsubscribe();
      }
    },
    [
      budgetService,
      categorizedCosts,
      categorizedCostsName,
      costs,
      costsName,
      farmId,
      farmUser,
      initialData,
      revenues,
      revenuesName,
      season,
      t,
    ]
  );

  return isLoading ? (
    <DashboardLoading />
  ) : (
    <FormProvider {...formMethods}>
      <form
        className={`${css['table']} ${css['table-sub-budget']}`}
        onSubmit={handleSubmit(onSubmit)}
      >
        <TableHeadings item={{}} />

        <RevenuesTable revenues={revenues} remove={removeCategory} />
        <AddButton
          addCategory={addCategory}
          categories={availableCategories}
          revenuesName={revenuesName}
        />

        <CategorizedCostsTable categorizedCosts={categorizedCosts} />
        <CostsTable costs={costs} />

        <div className={css['error']}>{error}</div>

        {allowedToStore(farmUser) && (
          <ButtonComponent
            name={'submit'}
            click={null}
            label={t('pages.finance.budget.save')}
            disabled={isLoading || isSavingData}
          />
        )}
      </form>
    </FormProvider>
  );
}

function allowedToStore(farmUser: FarmUser) {
  return (
    can(farmUser, { title: 'create budget' }) &&
    can(farmUser, { title: 'update budget' })
  );
}

function getBudgetParentName(itemName: string): string {
  const parts = itemName.split('_');
  parts.pop();
  return parts.join('_');
}
